import { get, minBy } from 'lodash';
import {
  differenceInMilliseconds,
  differenceInSeconds,
  isValid,
  format,
} from 'date-fns';

import { i18n } from '@lt/localization';
import { getFormattedDuration } from 'src/utils/dateUtils';
import {
  FLIGHT_TYPES_NAME,
  FLIGHT_CLASSES,
  FLIGHT_CLASSES_NAME,
} from 'src/routes/Checkout/CheckoutRoutes/PackageCheckout/store/flights/constants';
import type { FlightsData, TransformedBackFlight } from '../types/flights';

/**
 * Вырезаем таймзону, чтобы показать местное время
 * @param {string} date 2018-12-26T10:00:00.000+03:00
 * @returns {string} 2018-12-26T10:00:00
 */
export const getDateWithoutTimezone = (date: string) => {
  const [dateWithoutTimezone] = date.split('.');
  const parseDate = new Date(dateWithoutTimezone);

  const isValidDate = isValid(parseDate);
  return isValidDate ? dateWithoutTimezone : date;
};

/**
 * Возвращает время из даты безопасно, с фолбеком на текст
 * @param {string} date
 * @param {string} invalidText
 * @returns {string} HH:mm | Неизвестно
 */
export const getHoursSafe = (
  date: string,
  invalidText = i18n.t('checkout.sidebar.unknown'),
) => {
  const dateString = getDateWithoutTimezone(date);

  if (!dateString) return invalidText;

  const parseDate = new Date(dateString);
  return format(parseDate, 'HH:mm');
};

/**
 * Возвращает перелет с переданным flightPairId или самый дешевый перелет
 * @param flights Массив перелетов
 * @param flightPairId выбранный flight_pair_id
 * @returns Перелет с выбранным flight_pair_id или самый дешевый перелет
 */
export const findFlight = (
  backFlights: TransformedBackFlight[],
  flightPairId: string | null,
) =>
  flightPairId
    ? backFlights.find((flight) => flight.flight_pair_id === flightPairId)
    : backFlights[0];

/**
 * Возвращает ближайший перелет туда к указанному времени вылета
 * @param {string} departureTime "2018-12-26T10:00:00.000+03:00"
 * @param {Array} flights Полеты, в которых есть to
 * @param {boolean} isBusiness бизнес класс
 * @returns {object}
 */
const getClosestFlightTo = (
  departureTime: string,
  flights,
  isBusiness?: boolean,
) => {
  if (!departureTime) {
    throw new Error('Departure time is required');
  }

  if (!flights) {
    throw new Error('Array of flights is required');
  }

  let current = flights[0];

  if (!current.to || !current.to.departure) {
    throw new Error('Flight should contain "to" property with "departure"');
  }

  let diff = Math.abs(
    differenceInMilliseconds(
      new Date(departureTime),
      new Date(current.to.departure),
    ),
  );

  flights.forEach((item) => {
    if (Boolean(item.to.business) !== Boolean(isBusiness)) {
      return;
    }

    const newDiff = Math.abs(
      differenceInMilliseconds(
        new Date(departureTime),
        new Date(item.to.departure),
      ),
    );

    if (newDiff <= diff) {
      diff = newDiff;
      current = item;
    }
  });

  return current;
};

/**
 * Возвращает ближайший обратный перелет
 * @param {string} departureTime "2018-12-26T10:00:00.000+03:00"
 * @param {Array} flights Массив обратных перелетов
 * @param {boolean} isBusiness бизнес класс
 * @returns {object}
 */
const getClosestFlightBack = (
  departureTime: string,
  flights,
  isBusiness: boolean,
) => {
  if (!departureTime) {
    throw new Error('Departure time is required');
  }

  if (!flights) {
    throw new Error('Array of flights is required');
  }

  let current = flights[0];

  if (!current.departure) {
    throw new Error('Flight should contain "departure" property');
  }

  let diff = Math.abs(
    differenceInMilliseconds(
      new Date(departureTime),
      new Date(current.departure),
    ),
  );

  flights.forEach((item) => {
    if (Boolean(item.business) !== Boolean(isBusiness)) {
      return;
    }

    const newDiff = Math.abs(
      differenceInMilliseconds(
        new Date(departureTime),
        new Date(item.departure),
      ),
    );

    if (newDiff < diff) {
      diff = newDiff;
      current = item;
    }
  });

  return current;
};

type FlightsDepartureInfo = {
  to: string;
  back: string;
  business: boolean;
};

/**
 * Возвращает flight_pair_id ближайшей пары перелетов
 * @param {object} flightsDepartureInfo { to, back, business }
 * @param {array} flights
 */
export const getClosestFlightId = (
  flightsDepartureInfo: FlightsDepartureInfo,
  flights,
) => {
  if (!flightsDepartureInfo) {
    throw new Error('First argument (flight departures object) is required');
  }

  const { to, back, business } = flightsDepartureInfo;
  const closestFlightTo = getClosestFlightTo(to, flights, business);
  const closestFlightBack = getClosestFlightBack(
    back,
    closestFlightTo.back,
    business,
  );
  const id = closestFlightBack.flight_pair_id;

  return id;
};

// makes sloppy hash of flight object
export const makeFlightHash = ({ flight_id, business }) =>
  flight_id + Number(business);

export const getTransferCity = (transition) => get(transition, 'city.name');
export const getTransferDates = (data) => {
  const arrival = get(data, 'arrival');
  const departure = get(data, 'departure');

  return {
    arrival,
    departure,
  };
};
export const getTransferDuration = (data) => {
  const { arrival, departure } = getTransferDates(data);
  const diff = differenceInSeconds(new Date(departure), new Date(arrival));
  return diff ? getFormattedDuration(diff, 'м') : null;
};

/**
 * Возвращает тип и класс перелёта из данных о перелёте
 */
// TODO: выпилить warning, когда от всех туроператоров будет приходить поле type
// https://leveltravel.atlassian.net/browse/LT-11385
export const getFlightTypeData = ({
  type,
  warning: isRegular,
  business: isBusinessClass,
}: {
  type: TransformedBackFlight['type'];
  warning?: TransformedBackFlight['warning'];
  business: TransformedBackFlight['business'];
}) => ({
  flightType:
    type || (isRegular ? FLIGHT_TYPES_NAME.REGULAR : FLIGHT_TYPES_NAME.CHARTER),
  flightClass: isBusinessClass
    ? FLIGHT_CLASSES_NAME[FLIGHT_CLASSES.BUSINESS]
    : FLIGHT_CLASSES_NAME[FLIGHT_CLASSES.ECONOMY],
});

/**
 * Трансформер перелетов: добавляет поля в обратный перелет, а также добавляет
 * общую цену пакета в объект флайта для сортировки по всей цене
 * @param {array} flights
 * @param {object} packagePriceObject
 */
export const transformFlights = (flights, packagePriceObject): FlightsData[] =>
  flights
    ? flights.reduce((result, current) => {
        const {
          back,
          to,
          bonuses,
          flight_pair_id,
          warning,
          warranty,
          fuel_charge,
          availability,
          tour_id,
          operator_id,
        } = current;

        const total_package_price = fuel_charge + packagePriceObject[tour_id];

        const backFlight = {
          ...back,
          bonuses: bonuses || 0,
          flight_pair_id,
          warranty,
          warning,
          fuel_charge,
          availability,
          tour_id,
          operator_id,
        };

        result.push({
          to,
          back: [backFlight],
          total_package_price,
          isCheapest: null,
          isCheapestDirect: null,
        });

        return result;
      }, [])
    : [];

/**
 * Сортировка по полной цене перелета с ценой пакета
 * Также поднимаются в выдаче самый дешевый прямой
 */
export const sortFlights = (flights: FlightsData[]): FlightsData[] => {
  flights.sort(
    (firstFlight, secondFlight) =>
      firstFlight.total_package_price - secondFlight.total_package_price,
  );
  const cheapestDirectFlightIndex = flights.findIndex(
    (flight) => flight.isCheapestDirect && !flight.isCheapest,
  );
  if (cheapestDirectFlightIndex === -1) return flights;

  return [
    flights[0],
    flights[cheapestDirectFlightIndex],
    ...flights.filter(
      (_, idx) => idx !== 0 && idx !== cheapestDirectFlightIndex,
    ),
  ];
};

/**
 * Находит и помечает самый дешевый перелет
 */
const markCheapestFlight = (flights: FlightsData[]): FlightsData[] => {
  const cheapestFlight = minBy(flights, 'total_package_price');
  if (!cheapestFlight) return flights;

  const cheapestFlightIndex = flights.findIndex(
    (flight) =>
      // TODO: Вынести flight_pair_id выше по иерархии объекта (рядом с total_package_price)
      flight.back[0].flight_pair_id === cheapestFlight.back[0].flight_pair_id,
  );

  return [
    ...flights.slice(0, cheapestFlightIndex),
    { ...flights[cheapestFlightIndex], isCheapest: true },
    ...flights.slice(cheapestFlightIndex + 1, flights.length),
  ];
};

/**
 * Находит и помечает самый дешевый ПРЯМОЙ перелет
 */
const markCheapestDirectFlight = (flights: FlightsData[]): FlightsData[] => {
  const cheapestDirectFlight = minBy(
    flights.filter((flight) => flight.to.direct && flight.back[0].direct),
    'total_package_price',
  );
  if (!cheapestDirectFlight) return flights;

  const cheapestDirectFlightIndex = flights.findIndex(
    (flight) =>
      flight.back[0].flight_pair_id ===
      cheapestDirectFlight.back[0].flight_pair_id,
  );

  return [
    ...flights.slice(0, cheapestDirectFlightIndex),
    { ...flights[cheapestDirectFlightIndex], isCheapestDirect: true },
    ...flights.slice(cheapestDirectFlightIndex + 1, flights.length),
  ];
};

/**
 * Самый дешевый билет без пересадок ставим сразу же за самым дешевым
 */
export const markCheapestFlights = (flights: FlightsData[]): FlightsData[] => {
  const markedWithCheapestFlights = markCheapestFlight(flights);
  const markedWithCheapestDirectFlights = markCheapestDirectFlight(
    markedWithCheapestFlights,
  );

  return markedWithCheapestDirectFlights;
};

export const sortAndTransformFlights = (
  flights: FlightsData[],
): [FlightsData[], FlightsData[]] => {
  const economFlightsMarked = markCheapestFlights(
    flights.filter((flight) => !flight.to.business),
  );
  const businessFlightsMarked = markCheapestFlights(
    flights.filter((flight) => flight.to.business),
  );

  const economFlightsSorted = sortFlights(economFlightsMarked);
  const businessFlightsSorted = sortFlights(businessFlightsMarked);

  return [economFlightsSorted, businessFlightsSorted];
};
